import { Injectable, NgModule, Optional, SkipSelf } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { provideEffects } from '@ngrx/effects';
import {
  StoreRouterConnectingModule as NgrxStoreRouterConnectingModule,
  RouterStateSerializer,
} from '@ngrx/router-store';
import { StoreModule as NgrxStoreModule } from '@ngrx/store';
import { StoreDevtoolsModule as NgrxStoreDevtoolsModule } from '@ngrx/store-devtools';
import { mapResultsReducer } from '@shared/shared/modules/map/store';
import { articlesEffects, articlesReducer } from '@shared/store/articles';
import { apiErrorsReducer } from '@shared/store/error/api-errors.reducer';
import * as fromInspiration from '@shared/store/inspiration';
import { inspirationEffects } from '@shared/store/inspiration';
import { flightReducers, hotelReducers, travelEffects } from '@shared/store/travel';
import { environment } from '../../environment';
import { CustomAssetsEffects as customAssetEffects, reducers as customAssetReducers } from './customizable-assets';
import { effects as entertainmentEventEffects, reducers as entertainmentEventReducers } from './entertainment-event';
import * as fromEntertainmentReducers from './entertainment/reducers';
import { currentFilterEffects, filterEffects, reducers as filterReducers } from './filter';
import { RouterStateUrl } from './index';
import * as fromMemberEventEffects from './member-event/effects';
import * as fromMemberEventReducer from './member-event/reducers';
import { effects as memberProfileEffects, reducers as memberProfileReducer } from './member-profile';
import { payWithPointsReducers } from './pay-with-points';
import { PayWithPointsEffects } from './pay-with-points/pay-with-points.effects';
import { PreferencesEffects } from './preferences/effects';
import { preferencesReducers } from './preferences/reducers';
import { effects as restaurantEffects, reducers as restaurantReducers } from './restaurant';
import * as fromRouterReducer from './router';
import { sharedReducer } from './shared';
import * as fromTravelBookingEffects from './travel-booking/effects';
import * as fromTravelBookingReducer from './travel-booking/reducers';
import { travelInformationEffects, reducers as travelInformationReducers } from './travel-information';
import * as fromTravelSearchEffects from './travel-search/effects';
import * as fromCarHireSearchReducer from './travel-search/reducers';
import { effects as userEffects, reducers as userReducers } from './user';

@Injectable()
export class CustomRouterStateSerializer implements RouterStateSerializer<RouterStateUrl> {
  public serialize(routerState: RouterStateSnapshot): RouterStateUrl {
    const url = routerState.url;
    const queryParams = routerState.root.queryParams;

    let state: ActivatedRouteSnapshot = routerState.root;

    /**
     recursively crawl the router state tree (not ngrx, the ANGULAR router state tree)
     until the bottom of the tree (no first child anymore).
     once we reach the bottom we have access to the current route's params
     */
    while (state.firstChild) {
      state = state.firstChild;
    }
    const { params } = state;

    return { url, queryParams, params };
  }
}

const effects = [
  ...articlesEffects,
  ...inspirationEffects,
  ...restaurantEffects,
  ...fromTravelBookingEffects.effects,
  ...memberProfileEffects,
  ...fromTravelSearchEffects.travelSearchEffects,
  ...userEffects,
  ...entertainmentEventEffects,
  ...fromMemberEventEffects.effects,
  ...travelInformationEffects,
  ...filterEffects,
  ...currentFilterEffects,
  ...customAssetEffects,
  ...travelEffects,
  PayWithPointsEffects,
  PreferencesEffects,
];

@NgModule({
  imports: [
    NgrxStoreRouterConnectingModule.forRoot({ stateKey: 'router' }),
    NgrxStoreModule.forFeature('apiErrors', apiErrorsReducer),
    NgrxStoreModule.forFeature('entertainment-search', fromEntertainmentReducers.reducers),
    NgrxStoreModule.forFeature('shared', sharedReducer),
    NgrxStoreModule.forFeature('preferences', preferencesReducers),
    NgrxStoreModule.forFeature('articles', articlesReducer),
    NgrxStoreModule.forFeature('inspiration', fromInspiration.reducer),
    NgrxStoreModule.forFeature('map-results', mapResultsReducer),
    NgrxStoreModule.forFeature('restaurant', restaurantReducers),
    NgrxStoreModule.forFeature('travel-booking', fromTravelBookingReducer.reducers),
    NgrxStoreModule.forFeature('member-profile', memberProfileReducer),
    NgrxStoreModule.forFeature('travel-search', fromCarHireSearchReducer.reducers),
    NgrxStoreModule.forFeature('user', userReducers),
    NgrxStoreModule.forFeature('entertainment-event', entertainmentEventReducers),
    NgrxStoreModule.forFeature('member-event', fromMemberEventReducer.reducers),
    NgrxStoreModule.forFeature('router-state', fromRouterReducer.reducers),
    NgrxStoreModule.forFeature('travel-information', travelInformationReducers),
    NgrxStoreModule.forFeature('filters', filterReducers),
    NgrxStoreModule.forFeature('customAssets', customAssetReducers),
    NgrxStoreModule.forFeature('flight-search-data', flightReducers),
    NgrxStoreModule.forFeature('hotel-search-data', hotelReducers),
    NgrxStoreModule.forFeature('pay-with-points', payWithPointsReducers),
    NgrxStoreModule.forFeature('entertainment-ticket-search', fromEntertainmentReducers.ticketReducer),
    NgrxStoreModule.forFeature('entertainment-ticket-details-search', fromEntertainmentReducers.ticketDetailReducer),
    !environment.production ? NgrxStoreDevtoolsModule.instrument() : [],
  ],
  providers: [
    {
      /**
       * A custom serializer is required to reduce the default routerState object
       * to an object literal with the few properties we need (url/queryParams/params)
       */
      provide: RouterStateSerializer,
      useClass: CustomRouterStateSerializer,
    },
    provideEffects(effects),
  ],
})
export class StoreModule {
  constructor(
    @Optional()
    @SkipSelf()
    parentModule: StoreModule
  ) {
    // Prevent from importing this module twice.
    if (parentModule) {
      throw new Error('NgrxStoreModule is already loaded. Import it in the AppModule only.');
    }
  }
}
